// ExplanationPanel.component.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { getRiskBreakdown } from '@Actions';
import { selectRiskBreakdownEntry } from '@Selectors/RiskBreakdown.selectors';
import {
	selectRiskSetting,
	useSelectCofCategories,
	useSelectLofCategories,
} from '@Selectors/Risk.selectors';
import { NamespacedStoreState } from '@Types/store.types';
import { RiskDetailsRow } from '@Types/riskConfig.types';
import { BreakdownComponent } from '@Types/riskBreakdown.types';
import { toStoredAssetId } from '@Utils/riskAssetId';
import { AxisSection } from './AxisSection.component';
import { CoverageQualifier } from './CoverageQualifier.component';
import { CoverageSummary } from './CoverageSummary.component';
import {
	buildScoresOnly,
	computeCoverage,
	reconcileAxis,
	ReconciledAxis,
	ReconciledRisk,
	reconcileRisk,
	RiskMethod,
} from './explanation.utils';

// Identifies each fetch so a stale response can't fill a newer entry.
let nextRequestId = 0;

type RiskSetting = ReturnType<typeof selectRiskSetting>;

const pickRisk = (row: RiskDetailsRow, riskSetting: RiskSetting) => {
	const normalised = !!riskSetting.postProcessing?.some(
		p => p.type === 'normalize' && p.params?.useNormalize,
	);
	const riskFor = (method: RiskMethod) =>
		reconcileRisk({
			lof: row.CURRENT_LOF,
			cof: row.CURRENT_COF,
			method,
			rowRisk: row.CURRENT_RISK,
			normalised,
		});
	const primary = riskFor(riskSetting.RiskMethod);
	const otherMethod: RiskMethod =
		riskSetting.RiskMethod === 'multiplied' ? 'added' : 'multiplied';
	// Matrix models combine with the matrix config's method, which Results
	// doesn't load: try the other operator before calling it a mismatch.
	const alternate =
		riskSetting.riskType === 'matrix' && primary.outcome !== 'matches'
			? riskFor(otherMethod)
			: undefined;
	return alternate?.outcome === 'matches' ? alternate : primary;
};

// Names of the reconciliation checks that failed (spec D7: shown and logged).
const failedChecks = (
	lof: ReconciledAxis | undefined,
	cof: ReconciledAxis | undefined,
	risk: ReconciledRisk,
): string[] => {
	const failed: string[] = [];
	if (lof && !lof.pointsMatchTotal) failed.push('lofPointsMatchTotal');
	if (lof && !lof.matchesRow) failed.push('lofMatchesRow');
	if (cof && !cof.pointsMatchTotal) failed.push('cofPointsMatchTotal');
	if (cof && !cof.matchesRow) failed.push('cofMatchesRow');
	if (risk.outcome === 'mismatch') failed.push('riskMismatch');
	return failed;
};

interface Props {
	row: RiskDetailsRow;
	configId: string;
}

// Per-asset "why did this asset score this?" (I360A-2744). Fetches on expand;
// the store caches by config + asset and is cleared when a run completes.
export const ExplanationPanel = ({ row, configId }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const riskSetting = selectRiskSetting();
	// The breakdown endpoint matches the stored id, prefixed with the asset type.
	const assetId = toStoredAssetId(riskSetting.assetType, row);
	const entry = useSelector((s: NamespacedStoreState) =>
		selectRiskBreakdownEntry(s, configId, assetId),
	);
	const lofCategories = useSelectLofCategories();
	const cofCategories = useSelectCofCategories();

	const fetchBreakdown = useCallback(() => {
		nextRequestId += 1;
		dispatch(
			getRiskBreakdown({ configId, assetId, requestId: nextRequestId }),
		);
	}, [configId, assetId, dispatch]);

	useEffect(() => {
		if (!entry) fetchBreakdown();
	}, [entry, fetchBreakdown]);

	const loaded = entry?.status === 'loaded' ? entry.data : undefined;
	const breakdown = loaded?.breakdown ?? null;
	const lofRec = useMemo(
		() =>
			breakdown
				? reconcileAxis(breakdown.LOF, row.CURRENT_LOF)
				: undefined,
		[breakdown, row.CURRENT_LOF],
	);
	const cofRec = useMemo(
		() =>
			breakdown
				? reconcileAxis(breakdown.CoF, row.CURRENT_COF)
				: undefined,
		[breakdown, row.CURRENT_COF],
	);
	const risk = useMemo(() => pickRisk(row, riskSetting), [row, riskSetting]);

	// Warn once per distinct failing result, not on every render: the effect
	// re-runs only when the set of failed checks (or the asset) changes.
	const failedKey = loaded
		? failedChecks(lofRec, cofRec, risk).join(',')
		: '';
	useEffect(() => {
		if (!failedKey) return;
		console.warn('Risk explanation does not reconcile', {
			configId,
			assetId,
			check: failedKey.split(','),
		});
	}, [failedKey, configId, assetId]);

	if (!entry || entry.status === 'loading') {
		return (
			<Box sx={{ p: 3 }} data-cy="explanation-panel">
				<Skeleton
					variant="rectangular"
					height={160}
					data-cy="explanation-loading"
				/>
			</Box>
		);
	}
	if (entry.status === 'error' || !entry.data) {
		return (
			<Box sx={{ p: 3 }} data-cy="explanation-error">
				<Typography variant="body2" sx={{ mb: 1 }}>
					{/* eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here */}
					{t("Couldn't load the explanation for this asset.")}
				</Typography>
				<Button
					size="small"
					variant="outlined"
					onClick={fetchBreakdown}>
					{t('Retry')}
				</Button>
			</Box>
		);
	}

	const defaulted: BreakdownComponent[] = breakdown
		? [breakdown.LOF, breakdown.CoF].reduce<BreakdownComponent[]>(
				(all, a) =>
					all.concat(
						...a.categories.map(c =>
							c.components.filter(x => x.usedDefault),
						),
					),
				[],
		  )
		: [];

	return (
		<Box sx={{ p: 3 }} data-cy="explanation-panel">
			{breakdown && (
				<CoverageQualifier
					grade={row.GRADE}
					coverage={computeCoverage(breakdown)}
				/>
			)}
			<Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
				{breakdown && lofRec && cofRec ? (
					<>
						<AxisSection
							axisKey="LOF"
							axis={breakdown.LOF}
							reconciled={lofRec}
							configCategories={lofCategories}
						/>
						<AxisSection
							axisKey="COF"
							axis={breakdown.CoF}
							reconciled={cofRec}
							configCategories={cofCategories}
						/>
					</>
				) : (
					<>
						<AxisSection
							axisKey="LOF"
							scoresOnly={buildScoresOnly(
								lofCategories,
								row.WEIGHTED_SCORES,
								'LOF',
							)}
						/>
						<AxisSection
							axisKey="COF"
							scoresOnly={buildScoresOnly(
								cofCategories,
								row.WEIGHTED_SCORES,
								'COF',
							)}
						/>
					</>
				)}
				<CoverageSummary
					coverage={
						breakdown ? computeCoverage(breakdown) : undefined
					}
					defaulted={defaulted}
					risk={risk}
				/>
			</Box>
			{!breakdown && (
				<Typography
					variant="body2"
					sx={{ mt: 2, color: 'text.secondary' }}>
					{t(
						'Re-run this model to see the inputs, weights and data coverage behind each score.',
					)}
				</Typography>
			)}
		</Box>
	);
};

export default ExplanationPanel;
